import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  Inject,
  Injector,
  input,
  Input,
  LOCALE_ID,
  OnInit,
  Self,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NgControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ProductConfigurationService } from '@fleet/product-configuration';
import {
  dateFormatByLocale,
  dateWithTimeDateRequiredValidator,
  hasRequiredValidator,
} from '@fleet/utilities';
import { DateTime } from 'luxon';
import { TimeInputComponent } from '../time-input/time-input.component';
import {
  getTimeInvalidErrorMessage,
  getTimeRequiredErrorMessage,
  TimeDisplayMode,
  TimeOverride,
} from '../time-input/time-input.utilities';

@Component({
  selector: 'fleet-date-with-time-control',
  templateUrl: './date-with-time-control.component.html',
  styleUrls: ['./date-with-time-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DateWithTimeControlComponent
  implements OnInit, ControlValueAccessor
{
  /** When true, rejects combined values before now. */
  validateNotInPast = input(false);

  private readonly notInPastValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    if (!this.validateNotInPast()) {
      return null;
    }
    const value = control.value as DateTime | null;
    if (!value?.isValid) {
      return null;
    }
    const valueAtMinute = value.startOf('minute');
    const now = DateTime.now().startOf('minute');
    return valueAtMinute < now ? { BEFORE_TIME: 'true' } : null;
  };
  //NOTE FOR LATER - PARENT CAN WRITE VALUE DOWN INTO ERROR THAT A THING IS INCORRECT?
  //900 - 15min interval (step in seconds; 900/60 = 15-minute intervals)
  private inforcedMinuteIntervalSignal = signal<number | null>(null);
  _inforcedMinuteInterval: number = null;
  @Input() set inforcedMinuteInterval(value: number) {
    this._inforcedMinuteInterval = value;
    this.inforcedMinuteIntervalSignal.set(value ?? null);

    if (this.dateTimeForm) {
      this.syncSubControlValidatorsFromParent();
      this.dateTimeForm.updateValueAndValidity();
    }
  }
  get inforcedMinuteInterval() {
    return this._inforcedMinuteInterval;
  }

  /** Dynamic error message for invalid interval: e.g. "Please enter a time in 15-minute intervals (e.g., 00, 15, 30, 45 minutes)". */
  intervalErrorMessage = computed(() => {
    const stepSeconds = this.inforcedMinuteIntervalSignal();
    if (stepSeconds == null) return null;
    const intervalMinutes = stepSeconds / 60;
    if (60 % intervalMinutes !== 0) return null;
    const divider = 60 / intervalMinutes;
    const allowedMinutes = Array.from(
      { length: divider },
      (_, i) => i * intervalMinutes,
    );
    const examples = allowedMinutes
      .map((m) => String(m).padStart(2, '0'))
      .join(', ');
    return `Please enter a time in ${intervalMinutes}-minute intervals (e.g., ${examples} minutes)`;
  });

  private isTimePartRequired(): boolean {
    return (
      this.includeTime &&
      !!this.ngControl?.control?.hasValidator(Validators.required)
    );
  }

  private isDatePartRequired(): boolean {
    const ctrl = this.ngControl?.control;
    if (!ctrl) {
      return false;
    }
    return (
      ctrl.hasValidator(Validators.required) ||
      ctrl.hasValidator(dateWithTimeDateRequiredValidator)
    );
  }

  private syncSubControlValidatorsFromParent(): void {
    if (!this.dateTimeForm) {
      return;
    }

    const dateControl = this.dateTimeForm.get('date');
    const timeControl = this.dateTimeForm.get('time');

    // Do not use setValidators on date — it removes MatDatepickerInput NG_VALIDATORS
    // (matDatepickerParse for partial / invalid entry).
    if (dateControl) {
      dateControl.removeValidators(Validators.required);
      if (this.isDatePartRequired()) {
        dateControl.addValidators(Validators.required);
      }
    }

    // Do not use setValidators on time — it removes fleet-time-input NG_VALIDATORS
    // (invalidTime / invalidInterval for partial entry).
    if (timeControl) {
      timeControl.removeValidators(Validators.required);
      if (this.isTimePartRequired()) {
        timeControl.addValidators(Validators.required);
      }
    }

    dateControl?.updateValueAndValidity({ emitEvent: false });
    timeControl?.updateValueAndValidity({ emitEvent: false });
  }

  @Input() includeTime = true;
  @ViewChild('datePicker') datePicker: ElementRef;
  @ViewChild('date') datePickerOverlay: MatDatepicker<DateTime>;
  @ViewChild('timeInput') timeInput: TimeInputComponent;

  dateTimeForm: FormGroup;

  @Input() controlCustomClass: string;

  @Input() wrap = false;

  @Input() minDate: DateTime = null;
  @Input() maxDate: DateTime = null;

  /**
   * Wall clock applied when the user selects a date and the time field is still empty.
   * The picked date supplies the calendar day; the date portion of this value is ignored.
   * When unset or invalid, time is not auto-patched — the time field is focused instead.
   */
  _defaultTime: DateTime;
  @Input() set defaultTime(value: DateTime) {
    this._defaultTime = value;
    if (!this.dateTimeForm) {
      this.buildForm();
    }
    this.patchDefaultTimeWhenDateWithoutTime();
    this.changeDetectorRef.markForCheck();
  }

  get defaultTime() {
    return this._defaultTime;
  }

  get restoreFocusOnDatePickerClose(): boolean {
    if (!this.includeTime || !this.isTimePartRequired()) {
      return true;
    }
    return false;
  }

  private isTimeMissing(): boolean {
    const timeValue = this.dateTimeForm?.get('time')?.value as DateTime | null;
    return !timeValue || !timeValue.isValid;
  }

  dateFormat: string;

  @Input() timeLabel: string;
  @Input() dateLabel: string;
  /** Matches `fleet-time-input` display mode when product config has no `timeOverride`. */
  @Input() timeDisplayMode: TimeDisplayMode = 'locale';
  @Input() timePlaceholder = 'HH MM';
  @Input() datePlaceholder = 'Choose Date';

  @Input() timePrefix = 'schedule';
  @Input() datePrefix = 'event';

  @Input() labelClass = '';

  _futureDateOnly: boolean;
  @Input() set futureDateOnly(value: boolean) {
    this._futureDateOnly = value;
    if (!this.dateTimeForm) {
      this.buildForm();
    }
    if (value) {
      this.minDate = this.nowInConfiguredZone();
    } else {
      this.minDate = null;
    }
    this.dateTimeForm.updateValueAndValidity();
    this.changeDetectorRef.markForCheck();
  }

  get futureDateOnly() {
    return this._futureDateOnly;
  }

  private readonly timeOverride = signal<TimeOverride | null>(null);

  get timeRequiredErrorMessage(): string {
    return getTimeRequiredErrorMessage(
      this.locale,
      this.timeDisplayMode,
      this.timeOverride(),
    );
  }

  get timeInvalidErrorMessage(): string {
    return getTimeInvalidErrorMessage(
      this.locale,
      this.timeDisplayMode,
      this.timeOverride(),
    );
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private destroyRef: DestroyRef,
    private injector: Injector,
    private productConfigurationService: ProductConfigurationService,
    @Self() public ngControl: NgControl,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    ngControl.valueAccessor = this;

    if (!this.dateTimeForm) {
      this.buildForm();
    }

    this.dateTimeForm
      .get('time')
      .statusChanges.pipe(takeUntilDestroyed())
      .subscribe(() => this.changeDetectorRef.markForCheck());

    this.productConfigurationService.timeOverride$
      .pipe(takeUntilDestroyed())
      .subscribe((override) => {
        this.timeOverride.set(override);
        this.changeDetectorRef.markForCheck();
      });

    effect(() => {
      this.validateNotInPast();
      this.refreshOptInMinDate();
      this.syncNotInPastValidator();
      this.changeDetectorRef.markForCheck();
    });
  }

  /** When opt-in past validation is active, restrict picker to yesterday onward. */
  private refreshOptInMinDate(): void {
    if (this.validateNotInPast()) {
      this.minDate = DateTime.now().minus({ days: 1 });
      return;
    }
    if (this._futureDateOnly) {
      this.minDate = this.nowInConfiguredZone();
    }
  }

  private syncNotInPastValidator(): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    control.removeValidators(this.notInPastValidator);
    if (this.validateNotInPast()) {
      control.addValidators(this.notInPastValidator);
    }
    control.updateValueAndValidity({ emitEvent: false });
    this.syncBeforeTimeErrorsToSubControls();
    this.markTimeTouchedWhenDateMakesCombinedPast();
    this.changeDetectorRef.markForCheck();
  }

  private dateFieldTouched = false;

  /** Suppresses a follow-up `focus` from our post-open input refocus reopening the overlay. */
  private skipOpenDatePickerOnNextInputFocus = false;

  /** When true, `onDatePickerOpened` returns focus to the input after Material focuses the calendar. */
  private pendingInputFocusAfterPickerOpen = false;

  /** Picker is open or user is still interacting with the calendar overlay. */
  private datePickerSessionActive = false;

  /** True when the user began interacting with the date field while it already had a valid value. Suppresses auto-focus to time on edit. */
  private dateHadValidValueAtInteractionStart = false;

  /** Whether date parse / required errors should surface when date is optional. */
  get dateShouldShowValidationErrors(): boolean {
    if (this.isDatePartRequired()) {
      return true;
    }
    const dateControl = this.dateTimeForm?.get('date');
    const nativeValue = this.datePicker?.nativeElement?.value;
    if (nativeValue && nativeValue !== '') {
      return true;
    }
    return !!(
      dateControl?.hasError('matDatepickerParse') ||
      dateControl?.hasError('required')
    );
  }

  get showDateValidationErrors(): boolean {
    const dateControl = this.dateTimeForm?.get('date');
    return !!(
      this.dateFieldTouched ||
      dateControl?.touched ||
      this.ngControl?.control?.touched
    );
  }

  /** Whether composite datetime errors should surface on the time sub-control. */
  private shouldShowTimeValidationErrors(): boolean {
    if (!this.includeTime) {
      return false;
    }
    if (this.isTimePartRequired()) {
      return true;
    }
    const timeControl = this.dateTimeForm?.get('time');
    const timeValue = timeControl?.value as DateTime | null;
    if (timeValue && DateTime.isDateTime(timeValue) && timeValue.isValid) {
      return true;
    }
    return !!(
      timeControl?.hasError('invalidTime') ||
      timeControl?.hasError('invalidInterval')
    );
  }

  /** When the picked date + existing time is in the past, surface the error on the time field. */
  private markTimeTouchedWhenDateMakesCombinedPast(): void {
    if (!this.includeTime || !this.ngControl.control?.hasError('BEFORE_TIME')) {
      return;
    }
    const timeControl = this.dateTimeForm.get('time');
    if (this.shouldShowTimeValidationErrors() && timeControl) {
      timeControl.markAsTouched({ onlySelf: true });
    }
  }

  private nowInConfiguredZone(): DateTime {
    return DateTime.now();
  }

  private syncingErrorsFromParent = false;

  /** Push host control errors onto date/time sub-fields for mat-error display. */
  refreshErrorDisplay(): void {
    this.syncMinDateErrorsToSubControls();
    this.syncBeforeTimeErrorsToSubControls();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * WHY: `fleet-time-input` blur calls `updateValueAndValidity({ emitEvent: false })` on the time
   * sub-control, which wipes composite errors (e.g. `timeNotFuture`) injected from the host.
   * NO ALTERNATIVE: Host value/status does not change on blur-only focus, so host-driven sync never
   * re-runs; `statusChanges` also does not fire when `emitEvent: false`.
   * COUPLING: `fleet-time-input` segment blur handlers; re-sync via `refreshErrorDisplay()`.
   */
  onTimeFieldFocusOut(event: FocusEvent): void {
    const field = event.currentTarget as HTMLElement;
    if (field.contains(event.relatedTarget as Node)) {
      return;
    }
    queueMicrotask(() => {
      if (this.ngControl?.control?.invalid) {
        this.refreshErrorDisplay();
      }
    });
  }

  onDateFieldFocusOut(event: FocusEvent): void {
    if (this.datePickerSessionActive || this.datePickerOverlay?.opened) {
      return;
    }
    const related = event.relatedTarget as Node | null;
    if (
      related &&
      related instanceof Element &&
      related.closest('.cdk-overlay-container')
    ) {
      return;
    }
    if (!related && this.datePickerSessionActive) {
      return;
    }
    const field = event.currentTarget as HTMLElement;
    if (field.contains(event.relatedTarget as Node)) {
      return;
    }
    this.markDateFieldTouchedIfInvalid();
    this.resetDateInteractionFlagIfFullyCleared();
  }

  onDateInputBlur(): void {
    if (!this.datePickerOverlay?.opened) {
      return;
    }
    queueMicrotask(() => {
      if (this.datePickerOverlay?.opened || this.datePickerSessionActive) {
        this.clearDateFieldTouchState();
      }
    });
  }

  onDateInputFocus(): void {
    if (this.skipOpenDatePickerOnNextInputFocus) {
      this.skipOpenDatePickerOnNextInputFocus = false;
      return;
    }
    this.dateHadValidValueAtInteractionStart = this.isDatePartValid();
    if (!this.shouldOpenDatePickerOnInputInteraction()) {
      return;
    }
    this.openDatePickerKeepingInputFocus();
  }

  onDateInputPointerDown(): void {
    if (!this.datePickerOverlay?.opened) {
      return;
    }
    this.focusDateInputKeepingPickerOpen();
  }

  onDatePickerOpened(): void {
    this.datePickerSessionActive = true;
    this.dateHadValidValueAtInteractionStart = this.isDatePartValid();
    this.clearDateFieldTouchState();

    const shouldRefocusInput = this.pendingInputFocusAfterPickerOpen;
    this.pendingInputFocusAfterPickerOpen = false;
    if (shouldRefocusInput) {
      this.scheduleDateInputFocusAfterPickerOpen();
    }
  }

  private focusDateInputKeepingPickerOpen(): void {
    const input = this.datePicker?.nativeElement as
      | HTMLInputElement
      | undefined;
    if (!input || !this.datePickerOverlay?.opened) {
      return;
    }
    this.skipOpenDatePickerOnNextInputFocus = true;
    input.focus({ preventScroll: true });
  }

  /**
   * MatDatepickerContent focuses the active calendar cell in ngAfterViewInit, which runs
   * after a setTimeout(0). Defer refocus until after that render cycle.
   */
  private scheduleDateInputFocusAfterPickerOpen(): void {
    const input = this.datePicker?.nativeElement as
      | HTMLInputElement
      | undefined;
    if (!input) {
      return;
    }
    this.skipOpenDatePickerOnNextInputFocus = true;
    afterNextRender(
      () => {
        if (!this.datePickerOverlay?.opened) {
          return;
        }
        input.focus({ preventScroll: true });
        afterNextRender(
          () => {
            if (!this.datePickerOverlay?.opened) {
              return;
            }
            if (document.activeElement !== input) {
              input.focus({ preventScroll: true });
            }
            this.clearDateFieldTouchState();
          },
          { injector: this.injector },
        );
      },
      { injector: this.injector },
    );
  }

  private shouldOpenDatePickerOnInputInteraction(): boolean {
    if (this.datePickerOverlay?.opened) {
      return false;
    }
    if (this.ngControl?.control?.disabled) {
      return false;
    }
    const native = this.datePicker?.nativeElement?.value?.trim() ?? '';
    if (native) {
      return false;
    }
    return !this.isDatePartValid();
  }

  private openDatePickerKeepingInputFocus(): void {
    const overlay = this.datePickerOverlay;
    if (!this.datePicker?.nativeElement || !overlay || overlay.opened) {
      return;
    }
    this.pendingInputFocusAfterPickerOpen = true;
    overlay.open();
    if (!overlay.opened) {
      this.pendingInputFocusAfterPickerOpen = false;
    }
  }

  /** Copy minDateValidator / host errors onto date and time sub-controls for mat-error. */
  private syncMinDateErrorsToSubControls(): void {
    if (!this.dateTimeForm || !this.ngControl?.control) {
      return;
    }

    const controlErrors = this.ngControl.control.errors;
    if (controlErrors) {
      this.dateTimeForm.setErrors(controlErrors, { emitEvent: false });
    } else {
      this.dateTimeForm.setErrors(null, { emitEvent: false });
    }

    if (this.dateTimeForm.errors?.dateNotFuture) {
      const currentErrors = this.dateTimeForm.get('date')?.errors || {};
      this.dateTimeForm.get('date')?.setErrors(
        {
          ...currentErrors,
          dateNotFuture: this.dateTimeForm.errors.dateNotFuture,
          customDateErrorMessage:
            this.dateTimeForm.errors.customDateErrorMessage,
        },
        { emitEvent: false },
      );
    } else {
      const currentErrors = {
        ...(this.dateTimeForm.get('date')?.errors ?? {}),
      };
      delete currentErrors['dateNotFuture'];
      delete currentErrors['customDateErrorMessage'];
      this.dateTimeForm
        .get('date')
        ?.setErrors(Object.keys(currentErrors).length ? currentErrors : null, {
          emitEvent: false,
        });
    }

    if (this.includeTime) {
      const timeControl = this.dateTimeForm.get('time');
      if (
        this.shouldShowTimeValidationErrors() &&
        this.dateTimeForm.errors?.timeNotFuture
      ) {
        const currentErrors = timeControl?.errors || {};
        timeControl?.setErrors(
          {
            ...currentErrors,
            timeNotFuture: this.dateTimeForm.errors.timeNotFuture,
            customTimeErrorMessage:
              this.dateTimeForm.errors.customTimeErrorMessage,
          },
          { emitEvent: false },
        );
      } else if (timeControl) {
        const currentErrors = { ...(timeControl.errors ?? {}) };
        delete currentErrors['timeNotFuture'];
        delete currentErrors['customTimeErrorMessage'];
        timeControl.setErrors(
          Object.keys(currentErrors).length ? currentErrors : null,
          { emitEvent: false },
        );
      }
    }

    this.markMinDateErrorControlsTouched();
  }

  /** Surface min-date errors immediately when host re-validates without user touching this field. */
  private markMinDateErrorControlsTouched(): void {
    const formErrors = this.dateTimeForm.errors;
    if (!formErrors) {
      return;
    }

    if (formErrors.dateNotFuture) {
      const dateControl = this.dateTimeForm.get('date');
      if (dateControl && !dateControl.touched) {
        this.dateFieldTouched = true;
        dateControl.markAsTouched({ onlySelf: true });
      }
    }

    if (
      this.includeTime &&
      formErrors.timeNotFuture &&
      this.shouldShowTimeValidationErrors()
    ) {
      const timeControl = this.dateTimeForm.get('time');
      if (timeControl && !timeControl.touched) {
        timeControl.markAsTouched({ onlySelf: true });
      }
    }
  }

  /**
   * Composite errors (BEFORE_TIME, timeNotFuture) depend on date + time together —
   * they must not block recomputing the combined value when only the date changes.
   */
  private timeHasBlockingError(timeControl: AbstractControl | null): boolean {
    if (!timeControl?.invalid) {
      return false;
    }
    return (
      timeControl.hasError('required') ||
      timeControl.hasError('invalidTime') ||
      timeControl.hasError('invalidInterval')
    );
  }

  /** Parse date sub-control values from ISO strings or Luxon DateTime (Material Luxon adapter). */
  private toDateTime(value: unknown): DateTime | null {
    if (value == null) {
      return null;
    }
    if (DateTime.isDateTime(value)) {
      return value.isValid ? value : null;
    }
    if (typeof value === 'string') {
      const parsed = DateTime.fromISO(value);
      return parsed.isValid ? parsed : null;
    }
    return null;
  }

  /** Merge date/time sub-control values. */
  private buildCombinedDateTime(
    date: DateTime,
    time: DateTime | null,
  ): DateTime {
    if (time?.isValid) {
      return date.set({
        hour: time.hour,
        minute: time.minute,
        second: time.second,
        millisecond: time.millisecond,
      });
    }
    if (this.includeTime && this.isTimePartRequired()) {
      return DateTime.invalid('Empty time not allowed');
    }
    return date.set({ hour: 0, minute: 0 });
  }

  buildForm() {
    this.dateTimeForm = this.fb.group({
      date: [null],
      time: [null],
    });
  }

  ngOnInit(): void {
    this.dateFormat = dateFormatByLocale(this.locale);
    this.changeDetectorRef.markForCheck();
    if (!this.dateTimeForm) {
      this.buildForm();
    }

    const syncErrorsAndValidatorsFromParent = () => {
      if (this.syncingErrorsFromParent) {
        return;
      }
      this.syncingErrorsFromParent = true;
      try {
        this.syncSubControlValidatorsFromParent();

        if (hasRequiredValidator(this.ngControl.control)) {
          const currentValidators = this.ngControl.control.validator;
          const newValidators =
            Array.isArray(currentValidators) && currentValidators !== null
              ? [
                  ...currentValidators.filter(
                    (validator) => validator !== null,
                  ),
                  Validators.required,
                ]
              : currentValidators !== null
                ? [currentValidators, Validators.required]
                : [Validators.required];
          this.dateTimeForm.setValidators(newValidators);
        }

        this.syncMinDateErrorsToSubControls();

        this.syncBeforeTimeErrorsToSubControls();

        this.syncSubControlsTouchedFromHost();

        this.changeDetectorRef.markForCheck();
      } finally {
        this.syncingErrorsFromParent = false;
      }
    };

    // Run once on load
    syncErrorsAndValidatorsFromParent();
    this.syncNotInPastValidator();

    // Subscribe to status changes
    this.ngControl.control.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        syncErrorsAndValidatorsFromParent();
      });

    // Status might remain INVALID while the errors object changes (e.g. date change causes time to become invalid),
    // so also sync on value changes to keep internal controls + UI in sync.
    this.ngControl.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        syncErrorsAndValidatorsFromParent();
      });

    // Patch time from defaultTime when date is selected, or focus time when no defaultTime
    this.dateTimeForm
      .get('date')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dateValue: string | DateTime) => {
        if (!this.includeTime) {
          return;
        }

        const timeControl = this.dateTimeForm.get('time');
        const timeValue = timeControl.value as DateTime | null;
        const timeMissing = !timeValue || !timeValue.isValid;

        const base = this.toDateTime(dateValue);
        if (!base) {
          queueMicrotask(() => this.resetDateInteractionFlagIfFullyCleared());
        }
        if (base && timeMissing) {
          if (this._defaultTime?.isValid) {
            timeControl.patchValue(this.timeWhenDateChosenWithoutTime(base), {
              emitEvent: true,
            });
          } else if (this.shouldAutoFocusTimeAfterDateEntry()) {
            if (!this.datePickerOverlay?.opened) {
              this.focusTimeInput();
            }
          }
        } else if (base && !timeMissing) {
          // Date changed while time is already set — re-check combined datetime.
          this.revalidateCombinedAfterDateChange();
        }
      });

    this.dateTimeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: any) => {
        const timeVal = value?.time as DateTime | null | undefined;
        const hasOptionalTimeBranch =
          !this.includeTime ||
          !this.isTimePartRequired() ||
          (timeVal != null && timeVal.isValid);

        if (value && value.date && hasOptionalTimeBranch) {
          let date =
            this.toDateTime(value.date) ??
            (this.datePicker?.nativeElement?.value &&
            this.datePicker.nativeElement.value !== ''
              ? DateTime.invalid('Date invalid')
              : (this.includeTime &&
                    this.datePicker?.nativeElement?.value &&
                    this.datePicker.nativeElement.value !== '') ||
                  (this.includeTime &&
                    !this.datePicker?.nativeElement?.value &&
                    timeVal)
                ? DateTime.invalid('Time without date')
                : null);
          const time = timeVal ?? null;
          const timeControl = this.dateTimeForm.get('time');

          if (
            this.includeTime &&
            this.isTimePartRequired() &&
            time &&
            !time.isValid
          ) {
            this.onChange(null);
            this.syncNotInPastValidator();
            return;
          }

          if (date && !date.isValid) {
            this.onChange(null);
            this.syncNotInPastValidator();
            return;
          }

          if (!date) {
            this.onChange(null);
            this.syncNotInPastValidator();
            return;
          }

          if (time && time.isValid) {
            if (timeControl && this.timeHasBlockingError(timeControl)) {
              this.onChange(null);
              this.syncNotInPastValidator();
              return;
            }
            date = this.buildCombinedDateTime(date, time);
          } else if (this.includeTime && this.isTimePartRequired()) {
            date = DateTime.invalid('Empty time not allowed');
          } else {
            date = this.buildCombinedDateTime(date, null);
          }
          //Only emit good dates.

          this.onChange(date.isValid ? date : null);
          this.syncNotInPastValidator();
        } else {
          if (
            this.includeTime &&
            this.isTimePartRequired() &&
            this.datePicker?.nativeElement?.value !== ''
          ) {
            this.onChange(null);
          } else if (this.datePicker?.nativeElement?.value !== '') {
            this.onChange(DateTime.invalid('Input contains bad characters'));
          } else {
            this.onChange(null);
          }
          this.syncNotInPastValidator();
        }
      });
  }

  private syncBeforeTimeErrorsToSubControls(): void {
    const hostErrors = this.ngControl.control?.errors;
    const hasBeforeTime = !!hostErrors?.['BEFORE_TIME'];
    const dateControl = this.dateTimeForm.get('date');
    const timeControl = this.dateTimeForm.get('time');

    if (!hasBeforeTime) {
      if (dateControl) {
        const next = { ...(dateControl.errors ?? {}) };
        delete next['BEFORE_TIME'];
        dateControl.setErrors(Object.keys(next).length ? next : null, {
          emitEvent: false,
        });
      }
      if (timeControl) {
        const next = { ...(timeControl.errors ?? {}) };
        delete next['BEFORE_TIME'];
        timeControl.setErrors(Object.keys(next).length ? next : null, {
          emitEvent: false,
        });
      }
      return;
    }

    if (this.shouldShowTimeValidationErrors() && timeControl) {
      timeControl.setErrors(
        { ...timeControl.errors, BEFORE_TIME: 'true' },
        { emitEvent: false },
      );
      if (!timeControl.touched) {
        timeControl.markAsTouched({ onlySelf: true });
      }
      if (dateControl?.errors?.['BEFORE_TIME']) {
        const next = { ...dateControl.errors };
        delete next['BEFORE_TIME'];
        dateControl.setErrors(Object.keys(next).length ? next : null, {
          emitEvent: false,
        });
      }
    } else if (dateControl) {
      dateControl.setErrors(
        { ...dateControl.errors, BEFORE_TIME: 'true' },
        { emitEvent: false },
      );
      if (!dateControl.touched) {
        this.dateFieldTouched = true;
        dateControl.markAsTouched({ onlySelf: true });
      }
    }
  }

  /** Re-run combined date+time validation after the calendar date changes. */
  private revalidateCombinedAfterDateChange(): void {
    const dateControl = this.dateTimeForm.get('date');
    const timeControl = this.dateTimeForm.get('time');
    const date = this.toDateTime(dateControl?.value);
    const time = timeControl?.value as DateTime | null;

    if (!date) {
      return;
    }

    let combined: DateTime;
    if (time?.isValid && !this.timeHasBlockingError(timeControl)) {
      combined = this.buildCombinedDateTime(date, time);
    } else if (this.includeTime && this.isTimePartRequired()) {
      return;
    } else {
      combined = this.buildCombinedDateTime(date, null);
    }

    this.onChange(combined.isValid ? combined : null);
    this.syncNotInPastValidator();
  }

  /** Wall time from `defaultTime` on the picked calendar day. */
  private timeWhenDateChosenWithoutTime(base: DateTime): DateTime {
    const template = this._defaultTime;
    return base
      .set({
        hour: template.hour,
        minute: template.minute,
        second: template.second,
        millisecond: template.millisecond,
      })
      .startOf('minute');
  }

  onDatePickerClosed(): void {
    this.datePickerSessionActive = false;
    this.markDateFieldTouchedIfInvalid();
    if (!this.isDatePartValid()) {
      this.onTouched();
    }

    if (!this.includeTime || !this.isTimePartRequired()) {
      return;
    }
    if (!this.isDatePartValid()) {
      this.datePicker?.nativeElement?.focus();
      return;
    }
    if (this.shouldAutoFocusTimeAfterDateEntry()) {
      this.focusTimeInput();
      return;
    }
    this.datePicker?.nativeElement?.focus();
  }

  private clearDateFieldTouchState(): void {
    this.dateFieldTouched = false;
    const dateControl = this.dateTimeForm?.get('date');
    dateControl?.markAsUntouched({ onlySelf: true });
    this.changeDetectorRef.markForCheck();
  }

  private markDateFieldTouchedIfInvalid(): void {
    const dateControl = this.dateTimeForm?.get('date');
    if (!dateControl?.invalid) {
      return;
    }
    this.dateFieldTouched = true;
    dateControl.markAsTouched({ onlySelf: true });
    dateControl.updateValueAndValidity({ emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  private isDatePartValid(): boolean {
    const dateControl = this.dateTimeForm?.get('date');
    if (!dateControl) {
      return false;
    }
    const parsed = this.toDateTime(dateControl.value);
    if (!parsed) {
      const nativeValue = this.datePicker?.nativeElement?.value;
      if (nativeValue && nativeValue !== '') {
        return false;
      }
      return false;
    }
    if (
      dateControl.hasError('required') ||
      dateControl.hasError('matDatepickerParse') ||
      dateControl.hasError('dateNotFuture')
    ) {
      return false;
    }
    return true;
  }

  private shouldAutoFocusTimeAfterDateEntry(): boolean {
    return (
      this.includeTime &&
      this.isTimePartRequired() &&
      this.isTimeMissing() &&
      !this._defaultTime?.isValid &&
      !this.dateHadValidValueAtInteractionStart
    );
  }

  private resetDateInteractionFlagIfFullyCleared(): void {
    const base = this.toDateTime(this.dateTimeForm?.get('date')?.value);
    const native = this.datePicker?.nativeElement?.value?.trim() ?? '';
    if (!base && !native) {
      this.dateHadValidValueAtInteractionStart = false;
    }
  }

  private focusTimeInput(): void {
    setTimeout(() => {
      this.timeInput?.focus();
    }, 0);
  }

  /**
   * When `defaultTime` changes and we already have a date but no time yet, fill the time field.
   */
  private patchDefaultTimeWhenDateWithoutTime(): void {
    if (
      !this.includeTime ||
      !this.dateTimeForm ||
      !this._defaultTime?.isValid
    ) {
      return;
    }
    const dateValue = this.dateTimeForm.get('date').value as string | DateTime;
    const base = this.toDateTime(dateValue);
    if (!base) {
      return;
    }
    const timeControl = this.dateTimeForm.get('time');
    const timeValue = timeControl.value as DateTime | null;
    const timeMissing = !timeValue || !timeValue.isValid;
    if (!timeMissing) {
      return;
    }
    timeControl.patchValue(this.timeWhenDateChosenWithoutTime(base), {
      emitEvent: true,
    });
  }

  returnTimeWithHoursAndMinutes(
    dateTime: DateTime,
    hours: number,
    mins: number,
  ) {
    if (!isNaN(hours)) {
      dateTime = dateTime.set({ hour: hours });
    }
    if (!isNaN(mins)) {
      dateTime = dateTime.set({ minute: mins });
    }

    return dateTime;
  }

  onChange: (value: any) => void = () => undefined;
  onTouched: () => void = () => undefined;
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  writeValue(value: any) {
    if (value) {
      // Check if value is a date-only string (yyyy-mm-dd format)
      const isDateOnlyString =
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

      let dateTime: DateTime;
      if (typeof value === 'string') {
        dateTime = DateTime.fromISO(value);
      } else {
        dateTime = value as DateTime;
      }

      if (isDateOnlyString) {
        // Only patch the date part, leave time field untouched
        // Store as DateTime object for validators
        this.dateTimeForm.patchValue({ date: dateTime }, { emitEvent: false });
      } else {
        this.dateTimeForm.setValue(
          {
            date: value,
            time: dateTime.isValid ? dateTime.startOf('minute') : null,
          },
          { emitEvent: false },
        );
      }
      this.changeDetectorRef.markForCheck();
    } else {
      this.dateHadValidValueAtInteractionStart = false;
      this.dateTimeForm.reset({}, { emitEvent: false });
    }
    this.syncNotInPastValidator();
  }

  reset() {
    this.dateHadValidValueAtInteractionStart = false;
    this.dateTimeForm.reset({}, { emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  markControlAsTouched() {
    this.dateFieldTouched = true;
    this.dateTimeForm.markAllAsTouched();
    this.changeDetectorRef.markForCheck();
  }

  markControlAsUntouched(): void {
    this.datePickerSessionActive = false;
    this.dateFieldTouched = false;
    this.dateTimeForm.markAsUntouched({ onlySelf: true });
    this.timeInput?.markControlAsUntouched();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * WHY: Parent forms mark the host control touched on submit without touching inner date/time
   * sub-controls, so mat-form-field error state stays hidden even when the host is invalid.
   * COUPLING: `markControlAsTouched()` and parent wrappers that mark the host on validate.
   */
  private syncSubControlsTouchedFromHost(): void {
    const host = this.ngControl?.control;
    if (!host?.touched) {
      return;
    }
    this.dateFieldTouched = true;
    const dateControl = this.dateTimeForm.get('date');
    const timeControl = this.dateTimeForm.get('time');
    dateControl?.markAsTouched({ onlySelf: true });
    if (this.shouldShowTimeValidationErrors()) {
      timeControl?.markAsTouched({ onlySelf: true });
    }
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateTimeForm.disable({ emitEvent: false });
    } else {
      this.dateTimeForm.enable({ emitEvent: false });
    }
    this.dateTimeForm.updateValueAndValidity();
  }
}
